import React from 'react';
import { useMusic } from '../contexts/MusicContext';
import { QRCodeSVG } from 'qrcode.react';
import { SkipForward, Music, Disc3 } from 'lucide-react';
import { Button } from './ui/button';

const TransitionOverlay = ({ qrUrl }) => {
  const { transition, skipTransition } = useMusic();

  if (!transition) return null;

  const { track, countdown } = transition;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(10, 10, 10, 0.97)' }}
      data-testid="transition-overlay"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FF007F] rounded-full opacity-[0.06] blur-[180px]" />
      </div>

      <div className="relative z-10 text-center max-w-lg mx-auto px-6">
        {/* Spinning disc */}
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF007F]/20 mb-6 animate-logo-pulse">
          <Disc3 className="w-8 h-8 text-[#FF007F] animate-spin-slow" />
        </div>

        {/* "Up Next" label */}
        <p className="text-[#FF007F] text-xs font-bold uppercase tracking-[0.3em] mb-6">Up Next</p>

        {/* Track artwork + info */}
        <div className="flex flex-col items-center gap-5 mb-8">
          {track.album_image ? (
            <img
              src={track.album_image}
              alt={track.name}
              className="w-48 h-48 rounded-2xl object-cover shadow-2xl animate-slide-up"
              style={{ boxShadow: '0 20px 60px rgba(157, 0, 255, 0.25)' }}
              data-testid="transition-artwork"
            />
          ) : (
            <div className="w-48 h-48 rounded-2xl bg-white/5 flex items-center justify-center">
              <Music className="w-16 h-16 text-[#606060]" />
            </div>
          )}
          <div className="animate-slide-up stagger-1">
            <h2
              className="text-2xl sm:text-3xl font-extrabold text-white mb-1 tracking-wide"
              style={{ fontFamily: 'Outfit' }}
              data-testid="transition-title"
            >
              {track.name}
            </h2>
            <p className="text-[#A0A0A0] text-base" data-testid="transition-artist">
              {Array.isArray(track.artists) ? track.artists.join(', ') : ''}
            </p>
          </div>
        </div>

        {/* QR Code */}
        {qrUrl && (
          <div className="mb-6 animate-slide-up stagger-2">
            <p className="text-[#606060] text-[10px] uppercase tracking-[0.2em] font-semibold mb-3">Request a Song</p>
            <div className="inline-block bg-white rounded-xl p-3" data-testid="transition-qr">
              <QRCodeSVG value={qrUrl} size={120} level="H" fgColor="#000000" />
            </div>
          </div>
        )}

        {/* Countdown + Skip */}
        <div className="flex items-center justify-center gap-4 animate-slide-up stagger-3">
          {/* Countdown ring */}
          <div className="relative w-14 h-14">
            <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
              <circle cx="28" cy="28" r="24" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
              <circle
                cx="28" cy="28" r="24" fill="none"
                stroke="#FF007F"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 24}`}
                strokeDashoffset={`${2 * Math.PI * 24 * (1 - countdown / 10)}`}
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-lg" data-testid="transition-countdown">
              {countdown}
            </span>
          </div>

          <Button
            onClick={skipTransition}
            className="bg-white/10 hover:bg-white/20 text-white rounded-full px-5 h-10 text-sm font-semibold tracking-wider"
            data-testid="skip-transition-button"
          >
            <SkipForward className="w-4 h-4 mr-2" />
            PLAY NOW
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TransitionOverlay;
